import { InspectionPageEstimateInput } from '../buildInspectionPageEstimateInput';
import { INSPECTION_PDF_LABELS, INSPECTION_PDF_SAMPLE_VALUES } from './inspectionPdfLabels';
import { sectionHeading } from './inspectionPdfTypography';
import {
  pdfColumn,
  PdfContent,
  pdfRichText,
  pdfSinglePageColumn,
  pdfTable,
  PdfTableCell,
  pdfText,
} from './pdf/pdfBlocks';
import { PDF_CONTENT_WIDTH } from './pdf/pdfPage';
import { regularFont } from './pdf/pdfTextMetrics';

/** `labelParams`: 8 pt captions; the signature labels keep its bottom margin. */
const CAPTION_FONT = regularFont(8);
const SIGNATURE_LABEL_MARGIN = { bottom: 8 };
const SIGNATURE_LABELS_ROW_MARGIN = { top: 12 };
const SIGNATURE_LINE_MARGIN = { top: 12, bottom: 12 };
const SIGNATURE_LINE_COUNT = 3;
/** Column shares of the signature row (`CalculateColumnX.execute(0, 1/2)`, …): the widest one decides. */
const SIGNATURE_WIDEST_COLUMN = PDF_CONTENT_WIDTH / 2;

type ContactColumn = { caption: string; value: (width: number) => PdfContent };

/**
 * `PdfParticipantContactSectionFactory` with the table layout: the report author's name with the e-mail
 * and/or the phone, shown when either of them is switched on.
 */
export function participantContactsSection(input: InspectionPageEstimateInput): PdfContent {
  const { showUserEmails } = input;
  const { showPhoneNumbers } = input.fileMetadata;
  if (!showUserEmails && !showPhoneNumbers) {
    return undefined;
  }
  const columns: ContactColumn[] = [
    {
      caption: INSPECTION_PDF_LABELS.contactsUser,
      value: (width) => pdfText(INSPECTION_PDF_SAMPLE_VALUES.userName, width),
    },
    ...(showUserEmails
      ? [
          {
            caption: INSPECTION_PDF_LABELS.contactsEmail,
            value: (width: number) => pdfRichText(INSPECTION_PDF_SAMPLE_VALUES.userEmail, width),
          },
        ]
      : []),
    ...(showPhoneNumbers
      ? [
          {
            caption: INSPECTION_PDF_LABELS.contactsPhone,
            value: (width: number) => pdfRichText(INSPECTION_PDF_SAMPLE_VALUES.userPhone, width),
          },
        ]
      : []),
  ];
  const cells = (content: (column: ContactColumn, width: number) => PdfContent): PdfTableCell[] =>
    columns.map((column) => ({ content: (width) => [content(column, width)] }));

  return pdfColumn([
    sectionHeading(INSPECTION_PDF_LABELS.contactsHeading),
    pdfTable(
      {
        columnRatios: columns.map(() => 1),
        headerRow: cells(({ caption }, width) => pdfText(caption, width, { font: CAPTION_FONT })),
        rows: [cells(({ value }, width) => value(width))],
      },
      PDF_CONTENT_WIDTH
    ),
  ]);
}

/** `sectionApprovedBy`: the captions row and three dotted lines, never split across pages. */
export function signaturesSection(): PdfContent {
  const [widestCaption] = INSPECTION_PDF_LABELS.signatureColumns;
  return pdfSinglePageColumn([
    pdfColumn(
      [
        pdfText(widestCaption, SIGNATURE_WIDEST_COLUMN, {
          font: CAPTION_FONT,
          margin: SIGNATURE_LABEL_MARGIN,
        }),
      ],
      SIGNATURE_LABELS_ROW_MARGIN
    ),
    ...Array.from({ length: SIGNATURE_LINE_COUNT }, () =>
      pdfColumn([pdfText('.', SIGNATURE_WIDEST_COLUMN)], SIGNATURE_LINE_MARGIN)
    ),
  ]);
}
